import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Play } from "lucide-react";
import { motion } from "framer-motion";

export default function PageHero({
  title,
  text,
  primaryText = "Contact Us",
  primaryLink = "/contact",
  secondaryText = "Explore Solutions",
  secondaryLink = "/solutions",
  image,
  align = "left",
}) {
  const isCenter = align === "center";

  return (
    <section className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.06 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.8, ease: "easeOut" }}
      >
        <img
          src={image}
          alt={title}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      </motion.div>

      <div className="relative mx-auto flex min-h-screen max-w-7xl items-center px-6 pb-16 pt-36 sm:px-8 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
          className={`w-full ${
            isCenter ? "mx-auto max-w-4xl text-center" : "max-w-4xl"
          }`}
        >
          <div className="rounded-2xl bg-black/10 p-4 backdrop-blur-[2px]">
            <p className="mb-5 text-sm font-semibold uppercase tracking-[0.38em] text-white/70">
              Hydrotec Solutions
            </p>

            <h1
              className={`text-5xl font-semibold tracking-tight sm:text-6xl lg:text-7xl lg:leading-[0.95] ${
                isCenter ? "mx-auto max-w-4xl" : "max-w-4xl"
              }`}
            >
              {title}
            </h1>

            <p
              className={`mt-6 text-lg leading-8 text-white/85 sm:text-xl ${
                isCenter ? "mx-auto max-w-2xl" : "max-w-2xl"
              }`}
            >
              {text}
            </p>

            <div
              className={`mt-10 flex flex-wrap gap-4 ${
                isCenter ? "justify-center" : ""
              }`}
            >
              <Link
                to={primaryLink}
                className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-slate-950 transition duration-300 hover:bg-slate-200"
              >
                {primaryText}
                <ChevronRight className="ml-2 h-4 w-4" />
              </Link>

              <Link
                to={secondaryLink}
                className="inline-flex items-center justify-center rounded-full bg-white/15 px-6 py-3.5 text-sm font-semibold text-white backdrop-blur-md ring-1 ring-white/20 transition duration-300 hover:bg-white/25"
              >
                {secondaryText}
                <Play className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="flex flex-col items-center text-white/70">
          <span className="mb-2 text-[10px] uppercase tracking-[0.35em]">
            Scroll
          </span>
          <div className="flex h-10 w-6 justify-center rounded-full border border-white/30">
            <motion.div
              animate={{ y: [4, 16, 4] }}
              transition={{ repeat: Infinity, duration: 1.8 }}
              className="mt-1 h-2 w-2 rounded-full bg-white/80"
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
}